import { useState, useCallback } from 'react';

export interface WizardState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

export interface WizardsState {
  adGroupWizard: WizardState;
  rdaWizard: WizardState;
  rsaWizard: WizardState;
  assetGroupWizard: WizardState;
  demandGenAdWizard: WizardState;
  /** A4.3.2 — Meta create-ad-set wizard. Plain WizardState (no context
   *  generic) — campaign context flows through JSX props at the mount
   *  site in `CampaignStructure.tsx`, mirroring Google's `adGroupWizard`
   *  pattern. */
  metaAdSetWizard: WizardState;
  /** A4.3.3 — Meta create-ad wizard. Driven by the CreativeLibraryPicker
   *  modal at the mount site (picker opens on the "+ Create New Ad"
   *  click; the wizard opens once the picker emits a creativeSource). */
  metaAdWizard: WizardState;
}

/**
 * Hook to manage the open/close state of all creation wizards.
 */
export function useWizards(): WizardsState {
  const [isAdGroupWizardOpen, setIsAdGroupWizardOpen] = useState(false);
  const [isRDAWizardOpen, setIsRDAWizardOpen] = useState(false);
  const [isRSAWizardOpen, setIsRSAWizardOpen] = useState(false);
  const [isAssetGroupWizardOpen, setIsAssetGroupWizardOpen] = useState(false);
  const [isDemandGenAdWizardOpen, setIsDemandGenAdWizardOpen] = useState(false);
  const [isMetaAdSetWizardOpen, setIsMetaAdSetWizardOpen] = useState(false);
  const [isMetaAdWizardOpen, setIsMetaAdWizardOpen] = useState(false);

  const openAdGroupWizard = useCallback(() => setIsAdGroupWizardOpen(true), []);
  const closeAdGroupWizard = useCallback(() => setIsAdGroupWizardOpen(false), []);

  const openRDAWizard = useCallback(() => setIsRDAWizardOpen(true), []);
  const closeRDAWizard = useCallback(() => setIsRDAWizardOpen(false), []);

  const openRSAWizard = useCallback(() => setIsRSAWizardOpen(true), []);
  const closeRSAWizard = useCallback(() => setIsRSAWizardOpen(false), []);

  const openAssetGroupWizard = useCallback(() => setIsAssetGroupWizardOpen(true), []);
  const closeAssetGroupWizard = useCallback(() => setIsAssetGroupWizardOpen(false), []);

  const openDemandGenAdWizard = useCallback(() => setIsDemandGenAdWizardOpen(true), []);
  const closeDemandGenAdWizard = useCallback(() => setIsDemandGenAdWizardOpen(false), []);

  const openMetaAdSetWizard = useCallback(() => setIsMetaAdSetWizardOpen(true), []);
  const closeMetaAdSetWizard = useCallback(() => setIsMetaAdSetWizardOpen(false), []);

  const openMetaAdWizard = useCallback(() => setIsMetaAdWizardOpen(true), []);
  const closeMetaAdWizard = useCallback(() => setIsMetaAdWizardOpen(false), []);

  return {
    adGroupWizard: {
      isOpen: isAdGroupWizardOpen,
      open: openAdGroupWizard,
      close: closeAdGroupWizard,
    },
    rdaWizard: {
      isOpen: isRDAWizardOpen,
      open: openRDAWizard,
      close: closeRDAWizard,
    },
    rsaWizard: {
      isOpen: isRSAWizardOpen,
      open: openRSAWizard,
      close: closeRSAWizard,
    },
    assetGroupWizard: {
      isOpen: isAssetGroupWizardOpen,
      open: openAssetGroupWizard,
      close: closeAssetGroupWizard,
    },
    demandGenAdWizard: {
      isOpen: isDemandGenAdWizardOpen,
      open: openDemandGenAdWizard,
      close: closeDemandGenAdWizard,
    },
    metaAdSetWizard: {
      isOpen: isMetaAdSetWizardOpen,
      open: openMetaAdSetWizard,
      close: closeMetaAdSetWizard,
    },
    metaAdWizard: {
      isOpen: isMetaAdWizardOpen,
      open: openMetaAdWizard,
      close: closeMetaAdWizard,
    },
  };
}
